import { STAGE_ORB_PX } from '../orbSize';
import {
  ClusterDependency,
  ClusterDetails,
  ClusterInfrastructure,
  ClusterLayer,
  ClusterPod,
  ClusterService,
  DependencyLink,
  HealthTier,
  InfraGlyph,
  InfraK8sCluster,
  InfraNode,
  InfraPod,
  InfraPool,
  PodPlacement,
  PodVolume,
  PodsPage,
} from 'types/infraMap';

export type GraphNodeDetail =
  | { kind: 'cluster'; cluster: ClusterDetails; live: boolean }
  | { kind: 'service'; service: ClusterService }
  | { kind: 'dependency'; dependency: ClusterDependency }
  | { kind: 'pod'; pod: ClusterPod; service: ClusterService }
  | { kind: 'node' | 'pool' | 'k8scluster'; placement: PodPlacement }
  | { kind: 'volume'; volume: PodVolume; pod: string }
  | { kind: 'more'; serviceId: string; hidden: number }
  // The infrastructure lens: k8s cluster → node pool → node → this cluster's pods.
  | { kind: 'infraCluster'; k8s: InfraK8sCluster }
  | { kind: 'infraPool'; pool: InfraPool; k8s: InfraK8sCluster }
  | { kind: 'infraNode'; node: InfraNode; pool: InfraPool; k8s: InfraK8sCluster }
  | { kind: 'infraPod'; pod: InfraPod; node: InfraNode }
  // A stand-in while data loads: where the answer will land, or the cluster before its details.
  | { kind: 'ghost' };

export type GraphNodeKind = GraphNodeDetail['kind'];

export interface GraphNode {
  id: string;
  parent: string | null;
  label: string;
  tier: HealthTier;
  diameter: number;
  /** Whether clicking it can open a further layer. */
  expandable: boolean;
  detail: GraphNodeDetail;
  glyph?: InfraGlyph;
}

export const ROOT_ID = 'cluster';

// The cluster matches the flying orb it replaces (STAGE_ORB_PX); each layer steps down from it, so
// depth reads at a glance. A ghost takes the size of what it stands in for, so nothing jumps when it lands.
const DIAMETERS: Record<GraphNodeKind, number> = {
  cluster: STAGE_ORB_PX,
  service: 164,
  dependency: 140,
  pod: 124,
  node: 108,
  pool: 108,
  k8scluster: 108,
  volume: 96,
  more: 84,
  infraCluster: 164,
  infraPool: 140,
  infraNode: 124,
  infraPod: 108,
  ghost: 84,
};

const WORKLOAD_GLYPHS: Record<string, InfraGlyph> = { deployment: 'deployment', statefulset: 'statefulset' };

export function glyphOf(detail: GraphNodeDetail): InfraGlyph | undefined {
  switch (detail.kind) {
    case 'cluster':
      return 'namespace';
    case 'service':
      return WORKLOAD_GLYPHS[detail.service.kind ?? ''] ?? 'service';
    case 'dependency':
      return detail.dependency.kind;
    case 'pod':
    case 'infraPod':
      return 'pod';
    case 'node':
    case 'infraNode':
      return 'node';
    case 'pool':
    case 'infraPool':
      return 'pool';
    case 'k8scluster':
    case 'infraCluster':
      return 'k8s';
    case 'volume':
      return 'volume';
    case 'more':
      return 'more';
    default:
      return undefined;
  }
}

// How many stand-ins a loading layer shows: enough to hint at its shape, never a count.
const GHOST_SERVICES = 6;
const GHOST_PODS = 3;

const POD_PREFIX = 'pod:';

export const serviceNodeId = (serviceId: string) => `service:${serviceId}`;
export const podNodeId = (podName: string) => `${POD_PREFIX}${podName}`;
/** A pod's placement chain is named after the pod: `node:pod:<name>`, `pool:pod:<name>`. */
export const placementNodeId = (kind: 'node' | 'pool' | 'k8scluster', podName: string) =>
  `${kind}:${podNodeId(podName)}`;

/** Names of the pods among `ids` (the graph's expanded node ids). */
export const podNames = (ids: Iterable<string>) =>
  [...ids].filter(id => id.startsWith(POD_PREFIX)).map(id => id.slice(POD_PREFIX.length));

const node = (
  fields: Omit<GraphNode, 'diameter'> & { diameter?: number },
): GraphNode => ({ diameter: DIAMETERS[fields.detail.kind], glyph: glyphOf(fields.detail), ...fields });

// Scoped by pod: two pods on the same k8s node each get their own chain in the tree.
const placementChain = (podName: string, placement: PodPlacement) => {
  const chain: GraphNode[] = [];
  let parent = podNodeId(podName);
  const push = (
    kind: 'node' | 'pool' | 'k8scluster',
    label: string | null | undefined,
  ) => {
    if (!label) return;
    const id = placementNodeId(kind, podName);
    chain.push(
      node({ id, parent, label, tier: 'unknown', expandable: false, detail: { kind, placement } }),
    );
    parent = id;
  };
  push('node', placement.node?.name);
  push('pool', placement.pool?.name);
  push('k8scluster', placement.k8sCluster.code);
  return chain;
};

// A claim is named by its pod: two pods mounting the same claim name each show their own.
const volumeNodes = (podName: string, volumes: PodVolume[] = []) =>
  volumes.map(volume =>
    node({
      id: `volume:${podName}:${volume.claim}`,
      parent: podNodeId(podName),
      label: volume.claim,
      tier: volume.name ? 'healthy' : 'unknown',
      expandable: false,
      detail: { kind: 'volume', volume, pod: podName },
    }),
  );

const ghost = (parent: string, index: number, diameter: number) =>
  node({
    id: `ghost:${parent}:${index}`,
    parent,
    label: '',
    tier: 'unknown',
    expandable: false,
    diameter,
    detail: { kind: 'ghost' },
  });

const ghosts = (parent: string, count: number, diameter: number) =>
  Array.from({ length: count }, (_, index) => ghost(parent, index, diameter));

// Where a pod runs arrives as a chain, so its stand-in is one too: a node, then its pool.
const placementGhosts = (podId: string) => {
  const first = ghost(podId, 0, DIAMETERS.node);
  return [first, ghost(first.id, 0, DIAMETERS.pool)];
};

export interface GraphInput {
  /** Undefined while the cluster's first layer loads; `placeholder` then stands in for the root. */
  layer: ClusterLayer | undefined;
  placeholder?: { name: string; tier: HealthTier };
  expanded: Set<string>;
  podsByService: Map<string, PodsPage>;
  placementByPod: Map<string, PodPlacement>;
  /** Graph node ids whose next layer is being fetched; each shows ghost children until it lands. */
  loading?: Set<string>;
}

/** The visible tree, parents always before their children: cluster → services & dependencies → pods → placement. */
export function buildClusterGraph({
  layer,
  placeholder,
  expanded,
  podsByService,
  placementByPod,
  loading,
}: GraphInput): GraphNode[] {
  const isLoading = (id: string) => expanded.has(id) && Boolean(loading?.has(id));
  if (!layer) {
    if (!placeholder) return [];
    return [
      node({
        id: ROOT_ID,
        parent: null,
        label: placeholder.name,
        tier: placeholder.tier,
        expandable: false,
        diameter: DIAMETERS.cluster,
        detail: { kind: 'ghost' },
        glyph: 'namespace',
      }),
      ...(isLoading(ROOT_ID) ? ghosts(ROOT_ID, GHOST_SERVICES, DIAMETERS.service) : []),
    ];
  }

  const nodes: GraphNode[] = [
    node({
      id: ROOT_ID,
      parent: null,
      label: layer.cluster.name,
      tier: layer.cluster.tier,
      expandable: true,
      detail: { kind: 'cluster', cluster: layer.cluster, live: layer.live },
    }),
  ];
  if (!expanded.has(ROOT_ID)) return nodes;

  layer.services.forEach(service => {
    const id = serviceNodeId(service.id);
    nodes.push(
      node({ id, parent: ROOT_ID, label: service.name, tier: service.tier, expandable: true, detail: { kind: 'service', service } }),
    );
    const pods = expanded.has(id) ? podsByService.get(service.id) : undefined;
    if (!pods) {
      if (isLoading(id)) nodes.push(...ghosts(id, GHOST_PODS, DIAMETERS.pod));
      return;
    }

    pods.items.forEach(pod => {
      const podId = podNodeId(pod.name);
      nodes.push(
        node({ id: podId, parent: id, label: pod.name, tier: pod.tier, expandable: true, detail: { kind: 'pod', pod, service } }),
      );
      const placement = expanded.has(podId) ? placementByPod.get(pod.name) : undefined;
      if (placement) nodes.push(...placementChain(pod.name, placement), ...volumeNodes(pod.name, placement.volumes));
      else if (isLoading(podId)) nodes.push(...placementGhosts(podId));
    });

    const hidden = pods.total - pods.items.length;
    if (hidden > 0) {
      nodes.push(
        node({
          id: `more:${service.id}`,
          parent: id,
          label: `+${hidden} more`,
          tier: 'unknown',
          expandable: false,
          detail: { kind: 'more', serviceId: service.id, hidden },
        }),
      );
    }
  });

  layer.dependencies.forEach(dependency =>
    nodes.push(
      node({ id: dependency.id, parent: ROOT_ID, label: dependency.name, tier: 'unknown', expandable: false, detail: { kind: 'dependency', dependency } }),
    ),
  );
  return nodes;
}

export interface GraphLink {
  id: string;
  from: string;
  to: string;
  kind: DependencyLink['kind'];
}

const dependencyIds = (layer: ClusterLayer) => new Set(layer.dependencies.map(dependency => dependency.id));

/**
 * Who relies on what, as edges beside the tree: a service to the database or bucket it uses, the
 * gateway to the service it routes to. Only edges whose both ends are on the map are drawn.
 */
export function graphLinks(layer: ClusterLayer | undefined, nodes: GraphNode[]): GraphLink[] {
  if (!layer?.links) return [];
  const shown = new Set(nodes.map(each => each.id));
  const dependencies = dependencyIds(layer);
  const nodeIdOf = (id: string) => (dependencies.has(id) ? id : serviceNodeId(id));
  return layer.links
    .map(link => ({ id: `link:${link.from}:${link.to}`, from: nodeIdOf(link.from), to: nodeIdOf(link.to), kind: link.kind }))
    .filter(link => shown.has(link.from) && shown.has(link.to));
}

export const infraNodeIds = {
  k8s: (k8s: InfraK8sCluster) => `k8s:${k8s.id}`,
  pool: (pool: InfraPool) => `ipool:${pool.id}`,
  node: (k8s: InfraK8sCluster, each: InfraNode) => `inode:${k8s.id}/${each.name}`,
  pod: (pod: InfraPod) => `ipod:${pod.name}`,
};

export interface InfraGraphInput {
  layer: ClusterLayer | undefined;
  placeholder?: { name: string; tier: HealthTier };
  infra: ClusterInfrastructure | undefined;
  expanded: Set<string>;
}

/**
 * The same cluster infrastructure first: the cluster → the k8s clusters its pods run in → their node
 * pools → nodes → this cluster's pods on each. It arrives in one read, so only the first open waits.
 */
export function buildInfraGraph({ layer, placeholder, infra, expanded }: InfraGraphInput): GraphNode[] {
  const root = buildClusterGraph({
    layer,
    placeholder,
    expanded: new Set(),
    podsByService: new Map(),
    placementByPod: new Map(),
  });
  if (!layer || !expanded.has(ROOT_ID)) return root;
  if (!infra) return [...root, ...ghosts(ROOT_ID, 2, DIAMETERS.infraCluster)];

  const nodes = [...root];
  infra.k8sClusters.forEach(k8s => {
    const k8sId = infraNodeIds.k8s(k8s);
    nodes.push(node({ id: k8sId, parent: ROOT_ID, label: k8s.code ?? k8s.context, tier: k8s.tier, expandable: true, detail: { kind: 'infraCluster', k8s } }));
    if (!expanded.has(k8sId)) return;
    k8s.pools.forEach(pool => {
      const poolId = infraNodeIds.pool(pool);
      nodes.push(node({ id: poolId, parent: k8sId, label: pool.name, tier: pool.tier, expandable: true, detail: { kind: 'infraPool', pool, k8s } }));
      if (!expanded.has(poolId)) return;
      pool.nodes.forEach(each => {
        const nodeId = infraNodeIds.node(k8s, each);
        nodes.push(node({ id: nodeId, parent: poolId, label: each.name, tier: each.tier, expandable: true, detail: { kind: 'infraNode', node: each, pool, k8s } }));
        if (!expanded.has(nodeId)) return;
        each.pods.forEach(pod =>
          nodes.push(
            node({ id: infraNodeIds.pod(pod), parent: nodeId, label: pod.name, tier: pod.tier, expandable: false, detail: { kind: 'infraPod', pod, node: each } }),
          ),
        );
      });
    });
  });
  return nodes;
}

/** The node and everything under it, so collapsing also forgets what was open inside. */
export function subtreeIds(nodes: GraphNode[], rootId: string) {
  const ids = new Set([rootId]);
  nodes.forEach(candidate => {
    if (candidate.parent && ids.has(candidate.parent)) ids.add(candidate.id);
  });
  return ids;
}

/** From the cluster down to `id`, for the breadcrumb. */
export function ancestry(nodes: GraphNode[], id: string) {
  const byId = new Map(nodes.map(candidate => [candidate.id, candidate]));
  const path: GraphNode[] = [];
  let current = byId.get(id);
  while (current) {
    path.unshift(current);
    current = current.parent ? byId.get(current.parent) : undefined;
  }
  return path;
}
